// Sugalarm — /api/photo wrappers (cycle 27).
//
// The estimate endpoint is multipart/form-data, so it bypasses the
// JSON-body machinery in `request` and goes through `authedFetch`
// instead. The browser sets Content-Type with the multipart boundary
// automatically — we must NOT pass our own Content-Type header here.

import { authedFetch } from "../lib/apiClient";
import type { ApiError, ApiErrorKind, ApiResult } from "../lib/types";

export type PhotoConfidence = "high" | "medium" | "low";

export interface PhotoItem {
  name: string;
  portion?: string | null;
  carbs_g: number;
}

export interface PhotoEstimate {
  items: PhotoItem[];
  total_carbs_g: number;
  confidence: PhotoConfidence;
  notes: string | null;
  model: string;
  request_id: string;
}

function classify(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 415 || status === 422) return "validation";
  if (status === 413) return "validation";
  if (status === 429) return "rate_limit";
  if (status === 503) return "server";
  if (status >= 500) return "server";
  return "unknown";
}

async function readError(res: Response): Promise<ApiError> {
  let detail: unknown;
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const data = await res.json();
    detail = data;
    const d = (data as { detail?: unknown })?.detail;
    if (typeof d === "string") message = d;
  } catch {
    /* not JSON */
  }
  return {
    kind: classify(res.status),
    status: res.status,
    message,
    details: detail,
  };
}

export async function estimateCarbs(
  file: File,
): Promise<ApiResult<PhotoEstimate>> {
  const form = new FormData();
  form.append("image", file, file.name || "meal.jpg");
  let res: Response;
  try {
    res = await authedFetch("/api/photo/estimate_carbs", {
      method: "POST",
      body: form,
    });
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }
  if (!res.ok) return { ok: false, error: await readError(res) };
  try {
    const data = (await res.json()) as PhotoEstimate;
    return { ok: true, data };
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "unknown",
        status: res.status,
        message: e instanceof Error ? e.message : "bad response",
      },
    };
  }
}
